import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getActivePopups } from '../services/popupService';

const publicUrl = process.env.PUBLIC_URL || '';
const DISMISS_PREFIX = 'mitzva_popup_dismissed_';
const SHOW_DELAY_MS = 2000;

const resolveImage = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${publicUrl}${url}`;
};

const resolveCtaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('/')) return url;
  return `https://${url}`;
};

const hasPopupContent = (data) => {
  if (data.display_mode === 'full_image') return Boolean(data.full_image_url);
  return Boolean(data.title || data.subtitle || data.description || data.image_url);
};

const isDismissed = (popup) =>
  localStorage.getItem(`${DISMISS_PREFIX}${popup.id}_${popup.updated_at}`) === '1';

function WelcomePopup() {
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const popup = queue[currentIndex] || null;

  useEffect(() => {
    getActivePopups()
      .then((data) => {
        const pending = (data || []).filter((p) => hasPopupContent(p) && !isDismissed(p));
        setQueue(pending);
      })
      .catch(() => {});
  }, []);

  const scheduleShow = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
  }, []);

  useEffect(() => {
    if (queue.length > 0 && currentIndex < queue.length) {
      setOpen(false);
      scheduleShow();
    } else {
      setOpen(false);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [queue, currentIndex, scheduleShow]);

  const handleClose = () => {
    if (popup) {
      localStorage.setItem(`${DISMISS_PREFIX}${popup.id}_${popup.updated_at}`, '1');
    }
    setOpen(false);

    if (currentIndex < queue.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleCtaClick = (e) => {
    e.preventDefault();
    window.open(resolveCtaUrl(popup.cta_url), '_blank', 'noopener,noreferrer');
  };

  if (!popup) return null;

  const isFullImage = popup.display_mode === 'full_image';

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
      }}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '96vw', sm: '94vw' },
          maxWidth: '1400px',
          height: { xs: '94vh', sm: '92vh' },
          maxHeight: '94vh',
          m: 1,
          borderRadius: { xs: '12px', sm: '16px' },
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        aria-label="Cerrar"
        sx={{
          position: 'absolute',
          right: { xs: 10, sm: 16 },
          top: { xs: 10, sm: 16 },
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0,0.55)',
          color: '#fff',
          width: 44,
          height: 44,
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {isFullImage ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', minHeight: 0 }}>
          <Box
            component="img"
            src={resolveImage(popup.full_image_url)}
            alt={popup.title || popup.name || 'Anuncio'}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>
      ) : (
        <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden', display: 'flex', minHeight: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, width: '100%', height: '100%', minHeight: 0 }}>
            {popup.image_url && (
              <Box sx={{ flex: { xs: '0 0 auto', md: '0 0 50%' }, minHeight: { xs: 220, md: 0 }, height: { md: '100%' }, overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
                <Box
                  component="img"
                  src={resolveImage(popup.image_url)}
                  alt=""
                  sx={{ width: '100%', height: '100%', minHeight: { xs: 220, md: '100%' }, objectFit: 'cover', display: 'block' }}
                />
              </Box>
            )}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: { xs: 'center', md: 'flex-start' }, textAlign: { xs: 'center', md: 'left' }, p: { xs: 3, sm: 4, md: 5 }, pt: { xs: 5, md: 5 }, overflow: 'auto', minHeight: 0 }}>
              {popup.title && (
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#600002', mb: 1.5, lineHeight: 1.2, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' } }}>
                  {popup.title}
                </Typography>
              )}
              {popup.subtitle && (
                <Typography variant="h5" sx={{ fontWeight: 500, color: '#333', mb: 2, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                  {popup.subtitle}
                </Typography>
              )}
              {popup.description && (
                <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.8, mb: 4, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  {popup.description}
                </Typography>
              )}
              {popup.cta_label && popup.cta_url && (
                <Button
                  variant="contained"
                  component="a"
                  href={resolveCtaUrl(popup.cta_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCtaClick}
                  size="large"
                  sx={{
                    backgroundColor: '#600002',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.05rem',
                    borderRadius: '50px',
                    alignSelf: { xs: 'center', md: 'flex-start' },
                    '&:hover': { backgroundColor: '#E31C24' },
                  }}
                >
                  {popup.cta_label}
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default WelcomePopup;
